import MasterItemCollection from '../collection/MasterItemCollection';
import WebCADUtil from './WebCADUtil';

// 詳細の「名称」に出す項目（実データ確認後に差し替える可能性あり）
const DETAIL_NAME_FIELD: keyof MasterItemCollection = 'item_name';

export const ITEM_DETAIL_FIELD = {
  NAME: 'name',
  CODE: 'code',
  MAKER: 'maker',
  SIZE: 'size',
  PRICE: 'price',
} as const;
export type ItemDetailField = typeof ITEM_DETAIL_FIELD[keyof typeof ITEM_DETAIL_FIELD];

const DETAIL_LABELS: { readonly [key in ItemDetailField]: string } = {
  name: '名称',
  code: '品番',
  maker: 'メーカー',
  size: 'サイズ',
  price: '単価',
};
const ALL_DETAIL_FIELDS: readonly ItemDetailField[] = Object.keys(DETAIL_LABELS) as ItemDetailField[];
const SIZE_SEPARATOR = '*';
const PRICE_UNIT = '円';
const EMPTY_TEXT = '';
const POPUP_CLASS = 'selectItemDetailWrap';
const POPUP_SELECTOR = `.${POPUP_CLASS}`;
const DETAIL_ATTRIBUTE = 'data-detail';
const ACTIVE_CLASS = 'is-active';
const OPACITY_VISIBLE = '1';
// 吹き出しの矢印の長さ（約36px）＋サムネイルとの余白
const POPUP_GAP_PX = 46;
// 狭い画面で右へ寄せたときに、画面の左端から空ける余白
const POPUP_SCREEN_MARGIN_PX = 8;
const DETAIL_ROW_TEMPLATE = (field: ItemDetailField): string => `<div><label>${DETAIL_LABELS[field]}</label><span class="itemInfoWrap" ${DETAIL_ATTRIBUTE}="${field}"></span></div>`;
const POPUP_TEMPLATE = (rows: string): string => `<div class="${POPUP_CLASS}"><div class="inner">${rows}</div></div>`;
const CANCEL_BUTTON_ID = 'btnCancelItemSelect';
const APPLY_BUTTON_ID = 'btnApplyItemSelect';
const APPLY_FOOTER_HTML = `<button id="${CANCEL_BUTTON_ID}"><span class="jp">キャンセル</span><span class="en">Cancel</span></button>`
  + `<button id="${APPLY_BUTTON_ID}"><span class="jp">適用</span><span class="en">Apply</span></button>`;

/**
 * アイテム一覧でサムネイルを選んだときの詳細吹き出し
 */
export default class ItemDetailUtil {
  public static readonly TEXTURE_FIELDS: readonly ItemDetailField[] = [
    ITEM_DETAIL_FIELD.NAME, ITEM_DETAIL_FIELD.MAKER, ITEM_DETAIL_FIELD.CODE, ITEM_DETAIL_FIELD.PRICE,
  ];

  public static readonly FITTING_FIELDS: readonly ItemDetailField[] = [
    ITEM_DETAIL_FIELD.NAME, ITEM_DETAIL_FIELD.MAKER, ITEM_DETAIL_FIELD.CODE, ITEM_DETAIL_FIELD.SIZE, ITEM_DETAIL_FIELD.PRICE,
  ];

  // サイズの区切り文字（検索で入力の × や x をこれにそろえる）
  public static readonly SIZE_SEPARATOR: string = SIZE_SEPARATOR;

  // 選択→適用型ダイアログ共通のフッター
  public static readonly APPLY_FOOTER_HTML: string = APPLY_FOOTER_HTML;

  public static readonly CANCEL_BUTTON_ID: string = CANCEL_BUTTON_ID;

  public static readonly APPLY_BUTTON_ID: string = APPLY_BUTTON_ID;

  /**
   * 適用ボタンの有効・無効を切り替える（未選択では押せないようにする）
   */
  public static setApplyEnabled(button: Element, isEnabled: boolean): void {
    if (button) (button as HTMLButtonElement).disabled = !isEnabled;
  }

  /**
   * 吹き出しのHTML
   */
  public static createHtml(fields: readonly ItemDetailField[]): string {
    return POPUP_TEMPLATE(fields.map(DETAIL_ROW_TEMPLATE).join(EMPTY_TEXT));
  }

  /**
   * 吹き出しに値を入れる
   */
  public static fill(root: Element, master: MasterItemCollection): void {
    ALL_DETAIL_FIELDS.forEach((field) => {
      const target: Element = root.querySelector(`${POPUP_SELECTOR} [${DETAIL_ATTRIBUTE}="${field}"]`);
      if (target) target.textContent = ItemDetailUtil.getValue(field, master);
    });
  }

  /**
   * サムネイルの左に吹き出しを出す（吹き出しはダイアログ内に置き、位置の基準もダイアログ）
   */
  public static show(dialog: HTMLElement, thumbnail: Element): void {
    const popup: HTMLElement = dialog.querySelector(POPUP_SELECTOR);
    if (!popup) return;
    popup.style.opacity = OPACITY_VISIBLE;
    popup.classList.add(ACTIVE_CLASS);
    const dialogBound: DOMRect = dialog.getBoundingClientRect();
    const thumbBound: DOMRect = thumbnail.getBoundingClientRect();
    // 画面の幅が狭いときは、吹き出しの左端が画面からはみ出さないよう右へ寄せる
    const maxRight: number = dialogBound.right - popup.offsetWidth - POPUP_SCREEN_MARGIN_PX;
    popup.style.right = `${Math.min(dialogBound.right - thumbBound.left + POPUP_GAP_PX, maxRight)}px`;
    popup.style.top = `${thumbBound.top - dialogBound.top + (thumbBound.height - popup.offsetHeight) / 2}px`;
  }

  /**
   * 吹き出しを閉じる
   */
  public static hide(root: Element): void {
    root.querySelector(POPUP_SELECTOR)?.classList.remove(ACTIVE_CLASS);
  }

  /**
   * サイズの表示文字列（幅*奥行*高さ）。フリーワード検索でも同じ書式で照合する
   */
  public static formatSize(master: MasterItemCollection): string {
    return [master.item_width, master.item_depth, master.item_height].join(SIZE_SEPARATOR);
  }

  /**
   * サイズを持つか（データに無いサイズは0になるため、すべて0なら持たないとみなす）
   */
  public static hasSize(master: MasterItemCollection): boolean {
    return [master.item_width, master.item_depth, master.item_height].some((value) => Number(value) !== 0);
  }

  private static getValue(field: ItemDetailField, master: MasterItemCollection): string {
    switch (field) {
      case ITEM_DETAIL_FIELD.NAME: return String(master[DETAIL_NAME_FIELD] ?? EMPTY_TEXT);
      case ITEM_DETAIL_FIELD.CODE: return master.item_part_number;
      case ITEM_DETAIL_FIELD.MAKER: return master.item_maker_name;
      case ITEM_DETAIL_FIELD.SIZE: return ItemDetailUtil.formatSize(master);
      case ITEM_DETAIL_FIELD.PRICE: return `${WebCADUtil.priceFormat(master.item_unit_price)}${PRICE_UNIT}`;
      default: return EMPTY_TEXT;
    }
  }
}
